<script setup lang="ts">
/**
 * A deterministic avatar drawn from a seed string (#1697 O15): a mirrored five-by-five mark in one accent on one
 * dark ground, both theme.css tokens, so it reads as part of the console rather than a stock picture. The same seed
 * always draws the same image and a different seed a different one, so "Generate another" is only a new seed.
 *
 * Decorative unless given a `label`: the name beside it carries the meaning. `toPngDataUrl()` gives the drawn image
 * as the PNG data URL the member avatar upload accepts, built from the same pattern, so what is saved is what was
 * previewed.
 */
import { computed } from 'vue'
import { AVATAR_VIEWBOX, avatarPattern, avatarPngDataUrl, avatarSquares } from '../lib/generated-avatar'
import { kebab } from '../tokens'

const props = withDefaults(
    defineProps<{
        /** The same seed always draws the same image; a different one draws a different image. */
        seed: string
        /** What the image stands for, read out by a screen reader. Without one the avatar is decorative. */
        label?: string
    }>(),
    { label: '' },
)

const pattern = computed(() => avatarPattern(props.seed))
const squares = computed(() => avatarSquares(pattern.value))
const ground = computed(() => `var(--${kebab(pattern.value.ground)})`)
const accent = computed(() => `var(--${kebab(pattern.value.accent)})`)

/** The drawn image as a PNG data URL the member avatar upload accepts. */
async function toPngDataUrl(): Promise<string> {
    return avatarPngDataUrl(pattern.value)
}

defineExpose({ toPngDataUrl })
</script>

<template>
    <svg
        class="zh-generated-avatar"
        xmlns="http://www.w3.org/2000/svg"
        :viewBox="`0 0 ${AVATAR_VIEWBOX} ${AVATAR_VIEWBOX}`"
        shape-rendering="crispEdges"
        :role="label ? 'img' : undefined"
        :aria-label="label || undefined"
        :aria-hidden="label ? undefined : 'true'"
        data-testid="generated-avatar"
    >
        <rect :width="AVATAR_VIEWBOX" :height="AVATAR_VIEWBOX" :fill="ground" />
        <rect
            v-for="square in squares"
            :key="`${square.x}-${square.y}`"
            :x="square.x"
            :y="square.y"
            width="2"
            height="2"
            :fill="accent"
        />
    </svg>
</template>

<style scoped>
.zh-generated-avatar {
    display: block;
    width: 4rem;
    height: 4rem;
    flex: none;
}
</style>
